import React, { useEffect, useState } from 'react';
import {
  Table, Card, Tag, Input, Select, Button, Space, message, Row, Col,
  Modal, Form, Switch, Popconfirm, Tooltip,
} from 'antd';
import {
  SearchOutlined, ReloadOutlined, PlusOutlined, EditOutlined,
  DeleteOutlined, IdcardOutlined,
} from '@ant-design/icons';
import api from '../utils/api';

const { Option } = Select;

const CATEGORY_OPTIONS = [
  { value: 'AUSTRALIA_UNIVERSAL', label: 'AUSTRALIA_UNIVERSAL' },
];

const DEFAULT_IDENTITY_KEYS = {
  beneficiary: 'universal_beneficiary',
  payer: 'universal_payer',
};

const AdvertiserIdentities = () => {
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [bmList, setBmList] = useState([]);
  const [filters, setFilters] = useState({
    bm_id: undefined,
    status: undefined,
    search: '',
  });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchBmList = async () => {
    try {
      const res = await api.get('/business-managers/', { params: { limit: 100 } });
      setBmList(res.data || []);
    } catch (error) {
      console.error('Fetch BM list error:', error);
    }
  };

  const fetchList = async (page = pagination.current, pageSize = pagination.pageSize, nextFilters = filters) => {
    setLoading(true);
    try {
      const params = {
        skip: (page - 1) * pageSize,
        limit: pageSize,
      };
      if (nextFilters.bm_id) params.bm_id = nextFilters.bm_id;
      if (nextFilters.status) params.status = nextFilters.status;
      if (nextFilters.search?.trim()) params.search = nextFilters.search.trim();

      const res = await api.get('/advertiser-identities/', { params });
      const data = res.data || {};
      setItems(data.items || []);
      setTotal(data.total ?? 0);
      setPagination({ current: page, pageSize });
    } catch (error) {
      message.error(error.response?.data?.detail || '获取主体列表失败');
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBmList();
    fetchList(1, pagination.pageSize);
  }, []);

  const handleSearch = () => fetchList(1, pagination.pageSize);

  const handleReset = () => {
    const reset = { bm_id: undefined, status: undefined, search: '' };
    setFilters(reset);
    fetchList(1, pagination.pageSize, reset);
  };

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      entity_type: 'organization',
      regional_categories: ['AUSTRALIA_UNIVERSAL'],
      is_default: false,
      bm_id: filters.bm_id,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      bm_id: record.bm_id,
      display_name: record.display_name,
      identity_id: record.identity_id,
      status: record.status || 'active',
      entity_type: record.entity_type || 'organization',
      regional_categories: record.regional_categories?.length
        ? record.regional_categories
        : ['AUSTRALIA_UNIVERSAL'],
      is_default: !!record.is_default,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = {
        display_name: values.display_name.trim(),
        identity_id: values.identity_id.trim(),
        status: values.status,
        entity_type: values.entity_type,
        regional_categories: values.regional_categories || ['AUSTRALIA_UNIVERSAL'],
        identity_keys: DEFAULT_IDENTITY_KEYS,
        is_default: !!values.is_default,
      };

      if (editing) {
        await api.put(`/advertiser-identities/${editing.id}`, payload);
        message.success('已更新');
      } else {
        await api.post('/advertiser-identities/', {
          ...payload,
          bm_id: values.bm_id,
        });
        message.success('已创建');
      }
      setModalOpen(false);
      fetchList();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.detail || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      await api.delete(`/advertiser-identities/${record.id}`);
      message.success('已删除');
      fetchList();
    } catch (error) {
      message.error(error.response?.data?.detail || '删除失败');
    }
  };

  const columns = [
    {
      title: '主体名称',
      dataIndex: 'display_name',
      key: 'display_name',
      ellipsis: true,
      render: (text, record) => (
        <Space>
          <span>{text || '-'}</span>
          {record.is_default && <Tag color="blue">默认</Tag>}
        </Space>
      ),
    },
    {
      title: 'Identity ID',
      dataIndex: 'identity_id',
      key: 'identity_id',
      width: 180,
      render: (id) => <span style={{ fontFamily: 'monospace' }}>{id}</span>,
    },
    {
      title: '所属 BM',
      key: 'bm',
      width: 200,
      ellipsis: true,
      render: (_, record) => (
        <Tooltip title={`BM ID: ${record.facebook_bm_id || '-'}`}>
          <span>{record.bm_name || '-'}</span>
        </Tooltip>
      ),
    },
    {
      title: '区域类目',
      dataIndex: 'regional_categories',
      key: 'regional_categories',
      width: 200,
      render: (cats) =>
        (cats || []).length
          ? (cats || []).map((c) => <Tag key={c}>{c}</Tag>)
          : '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (s) => (
        <Tag color={s === 'active' ? 'green' : 'default'}>
          {s === 'active' ? '启用' : s === 'inactive' ? '停用' : s || '-'}
        </Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 140,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定删除该主体？"
            onConfirm={() => handleDelete(record)}
            okText="删除"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={(
          <Space>
            <IdcardOutlined />
            广告主主体
          </Space>
        )}
        extra={(
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => fetchList()}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新增主体
            </Button>
          </Space>
        )}
      >
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6}>
            <Select
              allowClear
              placeholder="按 BM 筛选"
              style={{ width: '100%' }}
              value={filters.bm_id}
              onChange={(v) => setFilters({ ...filters, bm_id: v })}
            >
              {bmList.map((bm) => (
                <Option key={bm.id} value={bm.id}>
                  {bm.name}
                </Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={8} md={4}>
            <Select
              allowClear
              placeholder="状态"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(v) => setFilters({ ...filters, status: v })}
            >
              <Option value="active">启用</Option>
              <Option value="inactive">停用</Option>
            </Select>
          </Col>
          <Col xs={24} sm={8} md={8}>
            <Input
              placeholder="搜索主体名称 / Identity ID"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              onPressEnter={handleSearch}
              allowClear
            />
          </Col>
          <Col>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                查询
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Col>
        </Row>

        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={items}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            showTotal: (t) => `共 ${t} 个主体`,
            onChange: (page, pageSize) => fetchList(page, pageSize),
          }}
          scroll={{ x: 960 }}
        />
      </Card>

      <Modal
        title={editing ? '编辑主体' : '新增主体'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            name="bm_id"
            label="所属 BM"
            rules={[{ required: true, message: '请选择 BM' }]}
          >
            <Select
              placeholder="选择 BM"
              disabled={!!editing}
              showSearch
              optionFilterProp="children"
            >
              {bmList.map((bm) => (
                <Option key={bm.id} value={bm.id}>
                  {bm.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="display_name"
            label="主体名称"
            rules={[{ required: true, message: '请输入主体名称' }]}
          >
            <Input placeholder="例如：长沙赫禧信息科技有限公司" />
          </Form.Item>
          <Form.Item
            name="identity_id"
            label="Identity ID"
            rules={[{ required: true, message: '请输入 Meta identity ID' }]}
          >
            <Input placeholder="Ads Manager 中的已验证主体 ID" />
          </Form.Item>
          <Form.Item name="regional_categories" label="区域类目" rules={[{ required: true }]}>
            <Select mode="multiple" options={CATEGORY_OPTIONS} />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="status" label="状态" rules={[{ required: true }]}>
                <Select>
                  <Option value="active">启用</Option>
                  <Option value="inactive">停用</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="entity_type" label="类型" rules={[{ required: true }]}>
                <Select>
                  <Option value="organization">组织</Option>
                  <Option value="person">个人</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="is_default" label="设为该 BM 默认主体" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdvertiserIdentities;
